import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { CreatorConfigItem, CreatorConfigRequest } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const initialForm: CreatorConfigRequest = {
  threshold: 10,
  rewardAmount: 5,
  isActive: true,
};

export const CreatorRewardConfigPanel = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreatorConfigRequest>(initialForm);

  /** 설정 목록 */
  const { data: configs, isLoading } = useQuery({
    queryKey: ['adminCreatorConfigs'],
    queryFn: () => rewardApi.getCreatorConfigs().then(res => res.data.results),
  });

  /** 생성 */
  const createMutation = useMutation({
    mutationFn: (params: CreatorConfigRequest) => rewardApi.createCreatorConfig(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCreatorConfigs'] });
      toast.success('설정이 추가되었습니다.');
      resetForm();
    },
    onError: () => toast.error('추가 실패'),
  });

  /** 수정 */
  const updateMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: CreatorConfigRequest }) =>
      rewardApi.updateCreatorConfig(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCreatorConfigs'] });
      toast.success('설정이 수정되었습니다.');
      resetForm();
    },
    onError: () => toast.error('수정 실패'),
  });

  /** 삭제 */
  const deleteMutation = useMutation({
    mutationFn: (id: number) => rewardApi.deleteCreatorConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCreatorConfigs'] });
      toast.success('설정이 삭제되었습니다.');
    },
    onError: () => toast.error('삭제 실패'),
  });

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setIsFormOpen(false);
  };

  const handleEdit = (config: CreatorConfigItem) => {
    setForm({
      threshold: config.threshold,
      rewardAmount: config.rewardAmount,
      isActive: config.isActive,
    });
    setEditingId(config.id);
    setIsFormOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('이 설정을 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, params: form });
    } else {
      createMutation.mutate(form);
    }
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">작성자 마일스톤 리워드 설정</h3>
        {!isFormOpen && (
          <Button size="sm" onClick={() => setIsFormOpen(true)} className="flex items-center gap-1">
            <Plus className="w-3 h-3" />
            추가
          </Button>
        )}
      </div>

      {/* 인라인 폼 */}
      {isFormOpen && (
        <Card className="border-blue-200 bg-blue-50/30">
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">참여자 수 (threshold)</label>
                <input
                  type="number"
                  min={1}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={form.threshold}
                  onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">지급 포인트</label>
                <input
                  type="number"
                  min={1}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  value={form.rewardAmount}
                  onChange={(e) => setForm({ ...form, rewardAmount: Number(e.target.value) })}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.isActive ?? true}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  활성
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                {editingId ? '수정' : '추가'}
              </Button>
              <Button size="sm" variant="outline" onClick={resetForm}>
                취소
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 설정 목록 */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-400">로딩 중...</div>
      ) : !configs?.length ? (
        <div className="text-center py-8 text-gray-400">설정이 없습니다.</div>
      ) : (
        <Card>
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3">
              설문에 N명 이상 참여하면 작성자에게 자동으로 포인트가 지급됩니다.
            </p>

            {/* 데스크톱 테이블 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-3">참여자 수</th>
                    <th className="pb-2 pr-3">지급 포인트</th>
                    <th className="pb-2 pr-3">상태</th>
                    <th className="pb-2">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((config: CreatorConfigItem) => (
                    <tr key={config.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-3 font-medium">{config.threshold}명</td>
                      <td className="py-2 pr-3 font-medium text-blue-600">{config.rewardAmount}개</td>
                      <td className="py-2 pr-3">
                        {config.isActive ? (
                          <Badge variant="outline" className="text-xs text-green-600">활성</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-gray-400">비활성</Badge>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 모바일 카드 */}
            <div className="md:hidden space-y-3">
              {configs.map((config: CreatorConfigItem) => (
                <div key={config.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{config.threshold}명 참여 시</span>
                    {config.isActive ? (
                      <Badge variant="outline" className="text-xs text-green-600">활성</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-gray-400">비활성</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-600 font-bold">{config.rewardAmount}개 지급</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
